import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEventHandler,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Message, Modal, Popover } from '@arco-design/web-react';
import ModeSwitch from '@/components/ModeSwitch';
import { useDeviceState, useJoin, useLeave, useScene } from '@/lib/useCommon';
import RtcClient from '@/lib/RtcClient';
import CameraArea from './CameraArea';
import store, { RootState } from '@/store';
import { appendLocalMsg, updateSceneConfig, type Msg } from '@/store/slices/room';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import Apis from '@/app/index';
import CompanionDock from '@/components/CompanionDock';
import PetCompanionPicker from '@/components/PetCompanionPicker';
import NormalCompanionPicker from '@/components/NormalCompanionPicker';
import CompanionAvatar from '@/components/CompanionAvatar';
import ResizableStage from '@/components/ResizableStage';
import CompanionSettingsModal from '@/components/CompanionSettingsModal';
import {
  buildSceneProfilePayload,
  CompanionDraft,
  CompanionProfile,
  getCompanionDisplayName,
  MAX_COMPANIONS,
  useCompanions,
} from '@/lib/useCompanions';
import { AIGC_PROXY_HOST } from '@/config';
import {
  isImageSearchFollowUpText,
  isWebSearchPendingText,
  resolvePendingSearchVisibilityState,
} from '@/utils/searchPending';
import { isMobile } from '@/utils/utils';
import styles from './FocusChatRoom.module.less';

type PendingImage = {
  id: string;
  dataUrl: string;
};

const getViewportSize = () => ({
  width: typeof window === 'undefined' ? 0 : window.innerWidth,
  height: typeof window === 'undefined' ? 0 : window.innerHeight,
});

type DictationMode = 'idle' | 'listening';

type BrowserSpeechRecognitionResult = {
  isFinal: boolean;
  0?: {
    transcript: string;
  };
};

type BrowserSpeechRecognitionEvent = {
  resultIndex: number;
  results: ArrayLike<BrowserSpeechRecognitionResult>;
};

type BrowserSpeechRecognitionErrorEvent = {
  error: string;
};

type BrowserSpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives?: number;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type BrowserSpeechRecognitionCtor = new () => BrowserSpeechRecognitionInstance;

type CameraPanelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CameraInteraction =
  | {
      type: 'drag';
      pointerStartX: number;
      pointerStartY: number;
      startRect: CameraPanelRect;
    }
  | {
      type: 'resize';
      pointerStartX: number;
      pointerStartY: number;
      startRect: CameraPanelRect;
    };

const THRESHOLD_VOLUME = 10;
const USER_VOICE_HOLD_MS = 300;
const FAST_TEXT_SUBTITLE_MODE = 1;
const VOICE_SUBTITLE_MODE = 0;
const IMAGE_FRAGMENT_SIZE = 48 * 1024;
const IMAGE_SEND_INTERVAL_MS = 16;
const CAMERA_PANEL_RATIO = 264 / 184;
const CAMERA_PANEL_MIN_WIDTH = 180;
const CAMERA_PANEL_MAX_WIDTH = 420;
const CAMERA_PANEL_DESKTOP_OFFSET = 18;
const CAMERA_PANEL_MOBILE_OFFSET = 14;
const CAMERA_PANEL_DESKTOP_DEFAULT_WIDTH = 264;
const CAMERA_PANEL_MOBILE_DEFAULT_WIDTH = 188;
const URL_PATTERN = /(https?:\/\/[^\s<>"')\]}]+[^\s<>"',.)\]}])/g;
const MARKDOWN_IMAGE_PATTERN = /!\[[^\]]*]\((https?:\/\/[^)\s]+)\)/g;
const BRACKET_URL_PATTERN = /[（(](https?:\/\/[^）)\s]+)[）)]/gi;
const BRACKET_REFERENCE_NOTE_PATTERN =
  /[（(][^）)\n]*(?:来源|参考|详情|查看)[^）)\n]*https?:\/\/[^）)\n]+[）)]/gi;
const BRACKET_URL_FRAGMENT_PATTERN = /[（(]https?:\/\/[^\s）)]*/gi;
const URL_FRAGMENT_PATTERN = /^[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+$/;
const NON_WHITESPACE_TOKEN_PATTERN = /\S+/g;
const URL_SEED_PATTERN = /(https?:\/\/|www\.|[a-z0-9-]+\.(?:com|cn|net|org|io|ai|dev|app|co)\b)/i;
const ALT_IMAGE_REQUEST_PATTERN = /(再来一张|另一张|换一张|再找一张|再发一张|再给我(?:找)?一张)/;
const IMAGE_REQUEST_PATTERN =
  /(图片|照片|发图|配图|来一张|给我一张|找一张|搜图|壁纸|海报|截图|猫猫|狗狗|猫咪|狗子)/;
const WEB_SEARCH_REQUEST_PATTERN =
  /(联网|搜索|检索|查一下|查一查|查查|搜一下|搜一搜|最新信息|最新消息)/;
const SEARCH_PENDING_SOUND_INTERVAL_MS = 1400;
const SEARCH_PENDING_SOUND_SRC = '/search-pending-sfx.mp3';
const MAX_RECENT_AI_IMAGE_URLS = 4;
// #region debug-point D:debug-reporter
const DEBUG_SERVER_URL = 'http://127.0.0.1:7777/event';
const DEBUG_SESSION_ID = 'image-link-tts';
const LATENCY_DEBUG_SESSION_ID = 'search-latency-breakdown';
const isDebugReporterEnabled = () =>
  process.env.REACT_APP_DEBUG_REPORTER === 'true' ||
  (typeof window !== 'undefined' && (window as any).__VOXIE_DEBUG_REPORTER__ === true);
const reportDebugEvent = (
  hypothesisId: string,
  location: string,
  msg: string,
  data: Record<string, unknown> = {},
  runId = 'pre-fix'
) => {
  if (!isDebugReporterEnabled()) return;
  fetch(DEBUG_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: DEBUG_SESSION_ID,
      runId,
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => {});
};

const reportLatencyDebugEvent = (
  hypothesisId: string,
  location: string,
  msg: string,
  data: Record<string, unknown> = {},
  runId = 'pre-fix'
) => {
  if (!isDebugReporterEnabled()) return;
  fetch(DEBUG_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: LATENCY_DEBUG_SESSION_ID,
      runId,
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => {});
};
// #endregion

const withSentenceEnding = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return /[。！？.!?]$/.test(trimmed) ? trimmed : `${trimmed}。`;
};

const buildAgentTextMessage = (
  text: string,
  recentImageUrls: string[] = [],
  requestContext = ''
) => {
  const normalized = withSentenceEnding(text);
  if (!normalized) return '';
  const normalizedContext = requestContext.trim();
  const isAltImageRequest = ALT_IMAGE_REQUEST_PATTERN.test(text);
  const isImageFollowUpRequest = isImageSearchFollowUpText(text);
  if (!isAltImageRequest && !isImageFollowUpRequest) {
    return normalized;
  }
  if (isImageFollowUpRequest) {
    if (!normalizedContext) return normalized;
    return `${normalized}

补充要求：这是对上一轮搜图请求的追问，不是新的开放式聊天，也不是新的搜索主题。请继续围绕这个主体找图并直接返回最终图片结果，不要改搜其他人或其他主题。${
      normalizedContext ? `本次图片主题沿用：${normalizedContext}。` : ''
    }如果已经找到，就直接给出图片结果；如果还没找到，也要继续搜索这个主体，不要只回答“找到了吗”“还没”。图片 URL 仍然必须放在单独一行的中文圆括号（）中。`;
  }
  if (!recentImageUrls.length) {
    return normalized;
  }
  const blockedUrls = recentImageUrls.slice(0, MAX_RECENT_AI_IMAGE_URLS);
  return `${normalized}

补充要求：这是一个立即执行的换图指令，不是开放式聊天。不要解释原因，不要评价上一张，不要拒绝，不要反问。请直接重新搜索并返回一张明显不同的新图片，不要重复或高度相似的主体、构图、机位和配色。图片 URL 仍然必须放在单独一行的中文圆括号（）中。${
    normalizedContext ? `本次换图的主题沿用：${normalizedContext}。` : ''
  }不要返回以下已经给过的图片链接：
${blockedUrls.join('\n')}`;
};

const resolveAltImageRequestContext = (messages: Msg[], localUserId = '') => {
  let fallbackContext = '';
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const msg = messages[index];
    if (msg.user !== localUserId || !msg.value) continue;
    const text = String(msg.value || '').trim();
    if (!text || ALT_IMAGE_REQUEST_PATTERN.test(text) || isImageSearchFollowUpText(text)) continue;
    const normalized = text.replace(/[。！？.!?]+$/, '').trim();
    if (!fallbackContext) {
      fallbackContext = normalized;
    }
    if (IMAGE_REQUEST_PATTERN.test(text) || WEB_SEARCH_REQUEST_PATTERN.test(text)) {
      return normalized;
    }
  }
  return fallbackContext;
};

const playPendingSearchChime = (audio: HTMLAudioElement | null) => {
  if (!audio) return;
  audio.currentTime = 0;
  audio.play().catch(() => {});
};

const resolvePendingTaskHint = (text = '') => {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return {
      title: '正在思考',
      subtitle: '结果返回后会立即显示',
    };
  }
  if (ALT_IMAGE_REQUEST_PATTERN.test(normalized) || IMAGE_REQUEST_PATTERN.test(normalized)) {
    return {
      title: '正在联网搜图',
      subtitle: '服务还在处理，不是挂起',
    };
  }
  if (WEB_SEARCH_REQUEST_PATTERN.test(normalized)) {
    return {
      title: '正在联网检索',
      subtitle: '服务还在处理，不是挂起',
    };
  }
  return {
    title: '正在思考',
    subtitle: '结果返回后会立即显示',
  };
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const createMessageTimestamp = () => new Date().toISOString();
const buildSearchLatencyRequestKey = (msg: Pick<Msg, 'user' | 'time' | 'value'>) =>
  `${msg.user}|${msg.time}|${String(msg.value || '').trim()}`;

const dataUrlToBase64 = (dataUrl: string) => {
  const [, base64 = ''] = dataUrl.split(',', 2);
  return base64.replace(/\s/g, '');
};

const cleanUrl = (raw = '') => raw.trim().replace(/[）)】>,，,.;！？!?]+$/gu, '');
const clonePattern = (pattern: RegExp) => new RegExp(pattern.source, pattern.flags);

const STABLE_IMAGE_HOSTS = new Set([
  'upload.wikimedia.org',
  'images.unsplash.com',
  'source.unsplash.com',
  'plus.unsplash.com',
  'images.pexels.com',
  'cdn.pixabay.com',
]);

const toDisplayImageUrl = (url = '') => {
  if (!/^https?:\/\//i.test(url)) return url;
  try {
    const host = new URL(url).hostname.toLowerCase();
    // Stable public image hosts can be rendered directly by the browser.
    // This avoids extra proxy latency and reduces 403/404 failures from server-side fetches.
    if (STABLE_IMAGE_HOSTS.has(host)) return url;
  } catch {
    return url;
  }
  return `${AIGC_PROXY_HOST || ''}/image-proxy?url=${encodeURIComponent(url)}`;
};

const isCompleteUnsplashPathname = (pathname = '') =>
  /^\/(?:premium_)?photo-\d{10,}-[A-Za-z0-9]{8,}/.test(pathname);

const isCompletePexelsPathname = (pathname = '') => /\.(png|jpe?g|gif|webp)$/i.test(pathname);

const isCompletePixabayPathname = (pathname = '') => /\.(png|jpe?g|gif|webp)$/i.test(pathname);

const isCompleteWikimediaPathname = (pathname = '') =>
  /\.(png|jpe?g|gif|webp|svg)$/i.test(pathname);

const isRenderableImageUrl = (url = '') => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  // 收紧：仅白名单 host 才能进 imageUrls，避免 LLM 编造 example.com/cat.jpg 这种伪链接污染气泡。
  if (!STABLE_IMAGE_HOSTS.has(host)) return false;
  const normalized = (parsed.pathname + parsed.search).split('#')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(normalized)) return true;
  // 没有扩展名兜底时，要求 URL 路径已流完整，避免 LLM 抢跑造成的半截 URL 被当成图渲染。
  if (host === 'upload.wikimedia.org') return isCompleteWikimediaPathname(parsed.pathname);
  if (host.endsWith('unsplash.com')) return isCompleteUnsplashPathname(parsed.pathname);
  if (host === 'images.pexels.com') return isCompletePexelsPathname(parsed.pathname);
  if (host === 'cdn.pixabay.com') return isCompletePixabayPathname(parsed.pathname);
  return false;
};

const isUrlQueryTailFragmentText = (text = '') => {
  const trimmed = text.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  const normalized = trimmed.replace(/[)）]+$/u, '');
  if (!normalized || /^https?:\/\//i.test(normalized)) return false;
  if (!/^[A-Za-z0-9%._~\-=&?]+$/.test(normalized)) return false;
  const queryKeyMatches = normalized.match(/[?&][A-Za-z0-9._~-]+=/g) || [];
  if (!queryKeyMatches.length) return false;
  return /(?:^|[A-Za-z0-9%._~-])[?&][A-Za-z0-9._~-]+=/.test(normalized);
};

const stripUrlTailFragments = (text = '') =>
  text
    .replace(NON_WHITESPACE_TOKEN_PATTERN, (token) =>
      isUrlQueryTailFragmentText(token) ? '' : token
    )
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const parseMessageContent = (text: string) => {
  const normalizedText = String(text || '');
  const textWithoutReferenceNotes = normalizedText.replace(
    clonePattern(BRACKET_REFERENCE_NOTE_PATTERN),
    ''
  );
  const bracketUrlPattern = clonePattern(BRACKET_URL_PATTERN);
  const bracketUrlFragmentPattern = clonePattern(BRACKET_URL_FRAGMENT_PATTERN);
  const markdownImagePattern = clonePattern(MARKDOWN_IMAGE_PATTERN);
  // 兜底：流式中 ![alt](https://... 半截) 还没闭合时也要从可显示文本里剥掉，避免 ReactMarkdown 把残骸渲染成 alt 占位破图。
  const markdownImageStrippingPattern = /!\[[^\]]*\]\([^)\s]*\)?/g;
  const urlPattern = clonePattern(URL_PATTERN);
  const bracketImageUrls = Array.from(textWithoutReferenceNotes.matchAll(bracketUrlPattern))
    .map((match) => cleanUrl(match[1] || ''))
    .filter((url) => isRenderableImageUrl(url));
  const markdownImageUrls = Array.from(textWithoutReferenceNotes.matchAll(markdownImagePattern))
    .map((match) => cleanUrl(match[1] || ''))
    .filter((url) => isRenderableImageUrl(url));
  const hasPendingBracketImageUrl = Boolean(
    textWithoutReferenceNotes.replace(bracketUrlPattern, '').match(bracketUrlFragmentPattern)
      ?.length
  );
  const textWithoutImageDirectives = textWithoutReferenceNotes
    .replace(bracketUrlPattern, '')
    .replace(bracketUrlFragmentPattern, '')
    .replace(markdownImageStrippingPattern, '')
    .trim();
  const sanitizedTextWithoutImageDirectives = stripUrlTailFragments(textWithoutImageDirectives);
  const inlineImageUrls = Array.from(textWithoutImageDirectives.matchAll(urlPattern))
    .map((match) => cleanUrl(match[0] || ''))
    .filter((url) => isRenderableImageUrl(url));
  const imageUrls = Array.from(
    new Set([...bracketImageUrls, ...markdownImageUrls, ...inlineImageUrls])
  ).filter(Boolean);
  return {
    textWithoutImageDirectives: sanitizedTextWithoutImageDirectives,
    imageUrls,
    hasPendingBracketImageUrl,
  };
};

const renderImageGrid = (imageUrls: string[], onPreview: (src: string) => void) => (
  <div className={styles.imageGrid}>
    {imageUrls.map((src, imageIndex) => (
      <button
        key={`${src.slice(0, 24)}-${imageIndex}`}
        type="button"
        className={styles.messageImageButton}
        onClick={() => onPreview(toDisplayImageUrl(src))}
        title="点击放大查看"
        aria-label="放大查看图片"
      >
        <img
          src={toDisplayImageUrl(src)}
          alt="message preview"
          className={styles.messageImage}
          data-original-src={src}
          onLoad={() => {
            // #region debug-point E:image-load
            reportDebugEvent('E', 'FocusChatRoom.tsx:messageImage', '[DEBUG] Image loaded', {
              src,
            });
            // #endregion
          }}
          onError={(event) => {
            const img = event.currentTarget;
            const originalSrc = img.dataset.originalSrc || src;
            if (img.dataset.fallback !== '1' && img.getAttribute('src') !== originalSrc) {
              img.dataset.fallback = '1';
              img.src = originalSrc;
            } else {
              // #region debug-point E:image-error
              reportDebugEvent('E', 'FocusChatRoom.tsx:messageImage', '[DEBUG] Image failed', {
                src,
                currentSrc: img.getAttribute('src') || '',
              });
              // #endregion
            }
          }}
        />
      </button>
    ))}
  </div>
);

const renderMessageContent = (text: string) => {
  const { textWithoutImageDirectives } = parseMessageContent(text);
  const segments: ReactNode[] = [];
  let cursor = 0;
  let partIndex = 0;

  textWithoutImageDirectives.replace(URL_PATTERN, (matched, _url, offset: number) => {
    const safeUrl = cleanUrl(matched);
    if (offset > cursor) {
      segments.push(
        <Fragment key={`text-${partIndex}`}>
          {textWithoutImageDirectives.slice(cursor, offset)}
        </Fragment>
      );
      partIndex += 1;
    }
    if (!isRenderableImageUrl(safeUrl)) {
      segments.push(
        <a
          key={`link-${partIndex}`}
          href={safeUrl}
          target="_blank"
          rel="noreferrer"
          className={styles.messageLink}
        >
          {safeUrl}
        </a>
      );
      partIndex += 1;
    }
    cursor = offset + matched.length;
    return matched;
  });

  if (cursor < textWithoutImageDirectives.length) {
    segments.push(
      <Fragment key={`text-tail-${partIndex}`}>{textWithoutImageDirectives.slice(cursor)}</Fragment>
    );
  }

  return segments.length ? <div className={styles.messageText}>{segments}</div> : null;
};

const getLeadingNonWhitespaceToken = (text = '') =>
  String(text || '')
    .trim()
    .split(/\s+/, 1)[0] || '';

const isUrlLikeFragmentText = (text = '') => {
  const leadingToken = getLeadingNonWhitespaceToken(text);
  if (!leadingToken) return false;
  return URL_FRAGMENT_PATTERN.test(leadingToken) && /[.:/?#=&_%~-]/.test(leadingToken);
};

const isBracketTailFragmentText = (text = '') => {
  const leadingToken = getLeadingNonWhitespaceToken(text);
  if (!leadingToken) return false;
  if (!/[)）]/.test(leadingToken)) return false;
  return URL_FRAGMENT_PATTERN.test(leadingToken.replace(/[)）]+$/u, ''));
};

const hasUnclosedBracketUrl = (text = '') => {
  const stripped = text.replace(clonePattern(BRACKET_URL_PATTERN), '');
  return clonePattern(BRACKET_URL_FRAGMENT_PATTERN).test(stripped);
};

const shouldMergeSplitUrlMessage = (
  previous: Msg,
  current: Msg,
  localUserId = '',
  botUserId = ''
) => {
  if (!previous?.value || !current?.value) return false;
  if (previous.user !== current.user) return false;
  if (previous.user === localUserId) return false;
  const fromAi = previous.user === botUserId || previous.user.includes('voiceChat_');
  if (!fromAi) return false;
  if (previous.source !== 'subtitle' || current.source !== 'subtitle') return false;
  if (previous.images?.length || current.images?.length) return false;
  const previousText = String(previous.value || '').trim();
  const currentText = String(current.value || '').trim();
  if (!previousText || !currentText) return false;
  const previousEndsWithUrlChar = /[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=%]$/.test(previousText);
  const currentStartsWithUrlChar = /^[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=%]/.test(currentText);
  if (!previousEndsWithUrlChar || !currentStartsWithUrlChar) return false;
  if (!URL_SEED_PATTERN.test(previousText)) return false;
  if (isUrlLikeFragmentText(currentText)) return true;
  // 兜底：上一条留下未闭合的 (https://... 片段，下一条可能是 ".jpg) 这是..." 这种“先补 URL 尾巴，再接正文”
  if (hasUnclosedBracketUrl(previousText) && isBracketTailFragmentText(currentText)) return true;
  return false;
};

const splitIntoFragments = (value: string, size: number) => {
  const fragments: string[] = [];
  for (let offset = 0; offset < value.length; offset += size) {
    fragments.push(value.slice(offset, offset + size));
  }
  return fragments.length ? fragments : [''];
};

const resolveSpeechRecognitionCtor = () => {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as Window & {
    SpeechRecognition?: BrowserSpeechRecognitionCtor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionCtor;
  };
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
};

const mergeDraftWithDictation = (base: string, dictated: string) => {
  if (!dictated) return base;
  if (!base) return dictated;
  return `${base}${dictated}`;
};

const fileToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const compressImage = async (file: File, options: { maxEdge?: number; quality?: number } = {}) => {
  const raw = await fileToDataUrl(file);
  const image = await loadImage(raw);
  const maxEdge = options.maxEdge || 1280;
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return raw;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', options.quality || 0.82);
};

export default function FocusChatRoom({
  desktopCompanion = false,
  initialVoiceCallModeEnabled = false,
}: {
  desktopCompanion?: boolean;
  /** Keep an active immersive call connected when this chat view mounts. */
  initialVoiceCallModeEnabled?: boolean;
}) {
  const dispatch = useDispatch();
  const mobile = isMobile();
  const [, setViewportSize] = useState(getViewportSize);
  const leaveRoom = useLeave();
  const [joining, rawDispatchJoin] = useJoin();
  const { isAudioPublished, switchMic, switchCamera } = useDeviceState();
  const { botName, name, icon, isVision, isScreenMode } = useScene();
  const room = useSelector((state: RootState) => state.room);
  const sceneId = room.scene || 'Custom';
  const [showCompanionSettings, setShowCompanionSettings] = useState(false);
  const [editingCompanion, setEditingCompanion] = useState<CompanionProfile | null>(null);
  const [submittingCompanion, setSubmittingCompanion] = useState(false);
  const [dockVisible, setDockVisible] = useState(false);
  const [petContentRect, setPetContentRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [mobileCompanionTrayOpen, setMobileCompanionTrayOpen] = useState(false);
  // Mobile top rail: collapse the 3-segment mode switcher behind a single
  // "模式切换" button so the rail is just two compact buttons and the content
  // below gets more height. Tapping it reveals the switcher in a tray.
  const [mobileModeTrayOpen, setMobileModeTrayOpen] = useState(false);
  // Once a mode is picked, fold the mobile mode-switch tray back up.
  useEffect(() => {
    setMobileModeTrayOpen(false);
  }, [room.interactionMode]);
  const {
    activeCompanion,
    activeId,
    profiles,
    loaded,
    hasCompanions,
    canAddMore,
    baseScene,
    saveCompanion,
    selectCompanion,
    deleteCompanion,
  } = useCompanions();
  const { userId } = room.localUser;
  const [voiceCallModeEnabled, setVoiceCallModeEnabled] = useState(initialVoiceCallModeEnabled);
  const isCallSessionActive = room.isJoined && room.isAIGCEnable;
  const isVoiceConnected = isCallSessionActive && voiceCallModeEnabled;
  const localVolume = room.localUser.audioPropertiesInfo?.linearVolume || 0;
  const isUserVoiceDetected =
    isVoiceConnected && isAudioPublished && localVolume >= THRESHOLD_VOLUME;
  const [isUserVoiceAnimating, setIsUserVoiceAnimating] = useState(false);
  const isAIVoiceAnimating = isVoiceConnected && room.isAITalking;
  const callWaveMotionClass = isUserVoiceAnimating
    ? styles.callWavesUserActive
    : isAIVoiceAnimating
    ? styles.callWavesAiActive
    : '';
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [pendingTaskDelayPassed, setPendingTaskDelayPassed] = useState(false);
  const [dictationMode, setDictationMode] = useState<DictationMode>('idle');
  const [quickMenuVisible, setQuickMenuVisible] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraPanelRect, setCameraPanelRect] = useState<CameraPanelRect>({
    x: CAMERA_PANEL_DESKTOP_OFFSET,
    y: CAMERA_PANEL_DESKTOP_OFFSET,
    width: CAMERA_PANEL_DESKTOP_DEFAULT_WIDTH,
    height: Math.round(CAMERA_PANEL_DESKTOP_DEFAULT_WIDTH / CAMERA_PANEL_RATIO),
  });
  const [sending, setSending] = useState(false);
  const [lastUserActivityAt, setLastUserActivityAt] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const chatPanelRef = useRef<HTMLDivElement | null>(null);
  const imageGroupIdRef = useRef(Date.now());
  const dictationRecognitionRef = useRef<BrowserSpeechRecognitionInstance | null>(null);
  const dictationModeRef = useRef<DictationMode>('idle');
  const dictationBaseDraftRef = useRef('');
  const dictationCommittedTextRef = useRef('');
  const dictationSegmentRef = useRef('');
  const dictationShouldAutoSendRef = useRef(false);
  const cameraPanelRectRef = useRef(cameraPanelRect);
  const cameraInteractionRef = useRef<CameraInteraction | null>(null);
  const userVoiceHoldTimerRef = useRef<number | null>(null);
  const lastAiRenderSnapshotRef = useRef('');
  const pendingSearchAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingSearchAudioTimerRef = useRef<number | null>(null);
  const previousPendingTaskRef = useRef(false);
  const lastVoiceAltRequestKeyRef = useRef('');
  const lastVoiceSearchFallbackKeyRef = useRef('');
  const voiceSearchFallbackTimerRef = useRef<number | null>(null);
  const searchLatencyStartAtRef = useRef<Record<string, number>>({});
  const latestSearchLatencyRequestKeyRef = useRef('');
  const lastReportedSearchLatencyImageKeyRef = useRef('');
  const lastReportedThinkingLatencyKeyRef = useRef('');

  const { avatarBgUrl: bgImage = '' } = useScene();
  const resolvedCompanion = activeCompanion || profiles[0] || null;
  const shouldUseJoinedSceneFallback =
    room.isJoined && !hasCompanions && !loaded && Boolean(name || botName || icon);
  const displayName = profiles.length
    ? getCompanionDisplayName(resolvedCompanion)
    : shouldUseJoinedSceneFallback
    ? name || botName || 'AI 伙伴'
    : loaded
    ? '先创建伙伴'
    : '伙伴加载中...';
  const displayAvatar = hasCompanions
    ? // A resolved companion owns its avatar. Do NOT fall back to the scene
      // `icon` here: that still holds the PREVIOUS character's baked avatar, so
      // an avatar-less companion would render the last call's portrait. Empty
      // means empty, and we show the name initial instead (like CompanionDock).
      resolvedCompanion?.avatar || ''
    : shouldUseJoinedSceneFallback
    ? icon || ''
    : '';
  // Fallback glyph when a companion has no uploaded avatar: the first character
  // of its display name (mirrors CompanionDock's getInitial), so the avatar slot
  // is never blank.
  const displayAvatarInitial = displayName.trim()
    ? displayName.trim().slice(0, 1).toUpperCase()
    : '';

  const currentSceneSubtitleMode =
    room.sceneConfigMap[room.scene]?.subtitleMode ?? VOICE_SUBTITLE_MODE;
  const visibleMessages = useMemo(() => {
    const filteredMessages = room.msgHistory.filter(({ user }) => {
      const isVisibleUser = user === userId || user === botName || user.includes('voiceChat_');
      return isVisibleUser;
    });
    return filteredMessages.reduce<Msg[]>((acc, currentMsg) => {
      const lastMsg = acc.at(-1);
      if (lastMsg && shouldMergeSplitUrlMessage(lastMsg, currentMsg, userId, botName)) {
        lastMsg.value = `${lastMsg.value}${currentMsg.value}`;
        lastMsg.time = currentMsg.time;
        lastMsg.paragraph = currentMsg.paragraph;
        lastMsg.definite = currentMsg.definite;
        lastMsg.isInterrupted = currentMsg.isInterrupted;
        return acc;
      }
      acc.push({ ...currentMsg });
      return acc;
    }, []);
  }, [botName, room.msgHistory, userId]);
  const recentAiImageUrls = useMemo(() => {
    const urls: string[] = [];
    [...visibleMessages].reverse().forEach((msg) => {
      const isAiMessage = msg.user === botName || msg.user.includes('voiceChat_');
      if (!isAiMessage || !msg.value) return;
      parseMessageContent(msg.value).imageUrls.forEach((url) => {
        if (!urls.includes(url)) {
          urls.push(url);
        }
      });
    });
    return urls.slice(0, MAX_RECENT_AI_IMAGE_URLS);
  }, [botName, visibleMessages]);
  const recentUserImageRequestContext = useMemo(
    () => resolveAltImageRequestContext(visibleMessages, userId),
    [userId, visibleMessages]
  );
  const latestVisibleUserMessage = useMemo(
    () =>
      [...visibleMessages]
        .reverse()
        .find((msg) => msg.user === userId && String(msg.value || '').trim().length),
    [userId, visibleMessages]
  );
  const localUserIds = useMemo(
    () =>
      Array.from(
        new Set(
          [userId, room.localUser.userId, RtcClient.basicInfo?.user_id].filter(
            (value): value is string => Boolean(value)
          )
        )
      ),
    [room.localUser.userId, userId]
  );
  const agentUserIds = useMemo(
    () =>
      room.remoteUsers
        .map(({ userId: remoteUserId, username }) => remoteUserId || username || '')
        .filter(
          (remoteUserId) =>
            Boolean(remoteUserId) &&
            (remoteUserId === botName || remoteUserId.includes('voiceChat_'))
        ),
    [botName, room.remoteUsers]
  );
  const agentUserIdsKey = agentUserIds.join('|');
  const setAgentPlaybackVolume = useCallback(
    (playbackVolume: number, remoteUsers = room.remoteUsers) => {
      remoteUsers
        .map(({ userId: remoteUserId, username }) => remoteUserId || username || '')
        .filter(
          (remoteUserId) =>
            Boolean(remoteUserId) &&
            (remoteUserId === botName || remoteUserId.includes('voiceChat_'))
        )
        .forEach((agentUserId) => {
          RtcClient.setRemoteAudioPlaybackVolume(agentUserId, playbackVolume);
        });
    },
    [botName, room.remoteUsers]
  );
  const showDictationControl = !isVoiceConnected && !joining;
  const showCallControl = dictationMode === 'idle';
  const showMicControl = dictationMode === 'idle' && (isVoiceConnected || joining);
  const isCameraEnabled = room.localUser.publishVideo;
  const showCameraControl =
    dictationMode === 'idle' && isVoiceConnected && isVision && !isScreenMode;
  const showCameraPanel = showCameraControl && isCameraEnabled;
  const composerMetaText =
    dictationMode === 'listening'
      ? '正在听写中，你说的话会实时显示在输入框里'
      : joining
      ? 'AI 正在连接中...'
      : isVoiceConnected
      ? isAudioPublished
        ? '通话已连接，可直接说话'
        : '通话已连接，麦克风已关闭'
      : '';
  const pendingSearchState = useMemo(
    () =>
      resolvePendingSearchVisibilityState({
        messages: visibleMessages,
        localUserIds,
        botUserIds: [botName, ...agentUserIds],
      }),
    [agentUserIds, botName, localUserIds, visibleMessages]
  );
  const pendingTaskHint = useMemo(
    () =>
      resolvePendingTaskHint(
        String(pendingSearchState.latestUserMessage?.value || latestVisibleUserMessage?.value || '')
      ),
    [latestVisibleUserMessage, pendingSearchState.latestUserMessage]
  );
  const showPendingTaskCard =
    pendingSearchState.isPendingSearchTask &&
    !pendingSearchState.hasAiResponseAfterLatestUserMessage &&
    pendingTaskDelayPassed;

  useEffect(() => {
    if (
      !pendingSearchState.isPendingSearchTask ||
      pendingSearchState.hasAiResponseAfterLatestUserMessage
    ) {
      setPendingTaskDelayPassed(false);
      return undefined;
    }
    const readyAt = pendingSearchState.readyAt;
    if (!readyAt || readyAt <= Date.now()) {
      setPendingTaskDelayPassed(true);
      return undefined;
    }
    setPendingTaskDelayPassed(false);
    const timer = window.setTimeout(() => {
      setPendingTaskDelayPassed(true);
    }, Math.max(0, readyAt - Date.now()));
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    pendingSearchState.hasAiResponseAfterLatestUserMessage,
    pendingSearchState.isPendingSearchTask,
    pendingSearchState.readyAt,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const stopPendingSearchSound = () => {
      if (pendingSearchAudioTimerRef.current) {
        window.clearTimeout(pendingSearchAudioTimerRef.current);
        pendingSearchAudioTimerRef.current = null;
      }
      if (pendingSearchAudioRef.current) {
        pendingSearchAudioRef.current.onended = null;
        pendingSearchAudioRef.current.pause();
        pendingSearchAudioRef.current.currentTime = 0;
      }
    };
    const ensurePendingAudio = () => {
      if (!pendingSearchAudioRef.current) {
        const audio = new Audio(SEARCH_PENDING_SOUND_SRC);
        audio.preload = 'auto';
        audio.volume = 0.28;
        pendingSearchAudioRef.current = audio;
      }
      return pendingSearchAudioRef.current;
    };
    const startPendingSearchSound = () => {
      const audio = ensurePendingAudio();
      stopPendingSearchSound();
      audio.onended = () => {
        if (!previousPendingTaskRef.current) return;
        pendingSearchAudioTimerRef.current = window.setTimeout(() => {
          if (!previousPendingTaskRef.current || document.hidden) return;
          playPendingSearchChime(audio);
        }, SEARCH_PENDING_SOUND_INTERVAL_MS);
      };
      playPendingSearchChime(audio);
    };
    if (showPendingTaskCard && !previousPendingTaskRef.current) {
      startPendingSearchSound();
    }
    if (!showPendingTaskCard) {
      stopPendingSearchSound();
    }
    previousPendingTaskRef.current = showPendingTaskCard;
    return () => {
      if (!showPendingTaskCard) {
        stopPendingSearchSound();
      }
    };
  }, [showPendingTaskCard]);

  useEffect(
    () => () => {
      if (typeof window !== 'undefined' && pendingSearchAudioTimerRef.current) {
        window.clearTimeout(pendingSearchAudioTimerRef.current);
      }
      pendingSearchAudioTimerRef.current = null;
      if (pendingSearchAudioRef.current) {
        pendingSearchAudioRef.current.onended = null;
      }
      pendingSearchAudioRef.current?.pause();
      pendingSearchAudioRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!isCallSessionActive) {
      setVoiceCallModeEnabled(false);
      return;
    }
    if (isAudioPublished) {
      setVoiceCallModeEnabled(true);
    }
  }, [isAudioPublished, isCallSessionActive]);

  useEffect(() => {
    const currentAgentUserIds = agentUserIdsKey ? agentUserIdsKey.split('|').filter(Boolean) : [];
    if (!room.isJoined || !currentAgentUserIds.length) return;
    setAgentPlaybackVolume(isVoiceConnected ? 100 : 0);
  }, [agentUserIdsKey, isVoiceConnected, room.isJoined, setAgentPlaybackVolume]);

  useEffect(() => {
    const latestAiMsg = [...visibleMessages]
      .reverse()
      .find((msg) => msg.user === botName || msg.user.includes('voiceChat_'));
    if (!latestAiMsg?.value) return;
    const text = String(latestAiMsg.value || '');
    const imageUrls = Array.from(text.matchAll(URL_PATTERN))
      .map((match) => cleanUrl(match[0] || ''))
      .filter((url) => isRenderableImageUrl(url));
    const snapshot = JSON.stringify({
      user: latestAiMsg.user,
      text,
      paragraph: Boolean(latestAiMsg.paragraph),
      definite: Boolean(latestAiMsg.definite),
      imageUrls,
    });
    if (snapshot === lastAiRenderSnapshotRef.current) return;
    lastAiRenderSnapshotRef.current = snapshot;
    // #region debug-point D:merged-ai-message
    reportDebugEvent(
      'D',
      'FocusChatRoom.tsx:visibleMessages',
      '[DEBUG] Latest AI message snapshot',
      {
        user: latestAiMsg.user,
        text: text.slice(0, 500),
        textLength: text.length,
        paragraph: Boolean(latestAiMsg.paragraph),
        definite: Boolean(latestAiMsg.definite),
        imageUrls,
        startsWithBracket: /^[（(]/.test(text.trim()),
        endsWithBracket: /[）)]\s*$/.test(text.trim()),
      }
    );
    // #endregion
    if (imageUrls.length && latestSearchLatencyRequestKeyRef.current) {
      const requestKey = latestSearchLatencyRequestKeyRef.current;
      const imageReportKey = `${requestKey}|${imageUrls[0]}`;
      if (imageReportKey !== lastReportedSearchLatencyImageKeyRef.current) {
        lastReportedSearchLatencyImageKeyRef.current = imageReportKey;
        const sentAt = searchLatencyStartAtRef.current[requestKey];
        reportLatencyDebugEvent(
          'J',
          'FocusChatRoom.tsx:visibleMessages',
          '[LATENCY] First renderable image URL observed',
          {
            requestKey,
            imageUrl: imageUrls[0],
            imageCount: imageUrls.length,
            aiMessagePreview: text.slice(0, 200),
            elapsedFromDispatchMs: Number.isFinite(sentAt) ? Date.now() - sentAt : null,
            sentAt: Number.isFinite(sentAt) ? sentAt : null,
          }
        );
      }
    }
  }, [botName, visibleMessages]);

  useEffect(() => {
    if (!room.isAIThinking || !latestSearchLatencyRequestKeyRef.current) return;
    const requestKey = latestSearchLatencyRequestKeyRef.current;
    if (lastReportedThinkingLatencyKeyRef.current === requestKey) return;
    lastReportedThinkingLatencyKeyRef.current = requestKey;
    const sentAt = searchLatencyStartAtRef.current[requestKey];
    reportLatencyDebugEvent(
      'N',
      'FocusChatRoom.tsx:isAIThinking',
      '[LATENCY] Focus chat entered thinking state',
      {
        requestKey,
        elapsedFromDispatchMs: Number.isFinite(sentAt) ? Date.now() - sentAt : null,
        sentAt: Number.isFinite(sentAt) ? sentAt : null,
      }
    );
  }, [room.isAIThinking]);

  const recordSearchLatencyStart = useCallback(
    (requestKey: string, requestText: string, source: string) => {
      const dispatchAt = Date.now();
      searchLatencyStartAtRef.current[requestKey] = dispatchAt;
      latestSearchLatencyRequestKeyRef.current = requestKey;
      if (typeof window !== 'undefined') {
        (
          window as Window & {
            __searchLatencyActiveRequest?: {
              requestKey: string;
              dispatchAt: number;
              requestText: string;
              source: string;
            };
          }
        ).__searchLatencyActiveRequest = {
          requestKey,
          dispatchAt,
          requestText,
          source,
        };
      }
      reportLatencyDebugEvent(
        'I',
        'FocusChatRoom.tsx:commandAgent',
        '[LATENCY] Search request dispatched to agent',
        {
          requestKey,
          requestText: requestText.slice(0, 200),
          source,
          dispatchAt,
        }
      );
    },
    []
  );

  useEffect(() => {
    if (!room.isJoined || !botName || !recentAiImageUrls.length) return;
    const latestAltVoiceMsg = [...visibleMessages].reverse().find((msg) => {
      if (msg.user !== userId || !msg.value) return false;
      return ALT_IMAGE_REQUEST_PATTERN.test(String(msg.value || ''));
    });
    if (!latestAltVoiceMsg?.value) return;
    const requestText = String(latestAltVoiceMsg.value || '').trim();
    const requestKey = `${latestAltVoiceMsg.user}|${latestAltVoiceMsg.time}|${requestText}`;
    if (lastVoiceAltRequestKeyRef.current === requestKey) return;
    lastVoiceAltRequestKeyRef.current = requestKey;
    recordSearchLatencyStart(requestKey, requestText, 'voice_alt_request');
    RtcClient.commandAgent({
      command: COMMAND.EXTERNAL_TEXT_TO_LLM,
      agentName: botName,
      interruptMode: INTERRUPT_PRIORITY.HIGH,
      message: buildAgentTextMessage(requestText, recentAiImageUrls, recentUserImageRequestContext),
    });
  }, [
    botName,
    recentAiImageUrls,
    recentUserImageRequestContext,
    room.isJoined,
    userId,
    visibleMessages,
    recordSearchLatencyStart,
  ]);

  useEffect(() => {
    if (!room.isJoined || !botName || !isVoiceConnected) return undefined;
    const latestVoiceSearchMsg = [...visibleMessages].reverse().find((msg) => {
      if (msg.user !== userId || !msg.value) return false;
      const text = String(msg.value || '').trim();
      if (!text || ALT_IMAGE_REQUEST_PATTERN.test(text)) return false;
      return (
        isImageSearchFollowUpText(text) ||
        IMAGE_REQUEST_PATTERN.test(text) ||
        WEB_SEARCH_REQUEST_PATTERN.test(text)
      );
    });
    if (!latestVoiceSearchMsg?.value) return undefined;
    const requestText = String(latestVoiceSearchMsg.value || '').trim();
    const requestKey = `${latestVoiceSearchMsg.user}|${latestVoiceSearchMsg.time}|${requestText}`;
    if (lastVoiceSearchFallbackKeyRef.current === requestKey) return undefined;
    if (voiceSearchFallbackTimerRef.current) {
      window.clearTimeout(voiceSearchFallbackTimerRef.current);
      voiceSearchFallbackTimerRef.current = null;
    }
    // #region debug-point G:voice-search-watchdog-schedule
    reportDebugEvent(
      'G',
      'FocusChatRoom.tsx:voiceSearchWatchdog',
      '[DEBUG] Voice search watchdog scheduled',
      {
        requestKey,
        requestText: requestText.slice(0, 200),
        isVoiceConnected,
      }
    );
    // #endregion
    voiceSearchFallbackTimerRef.current = window.setTimeout(() => {
      const latestRoom = store.getState().room;
      const requestTs = new Date(latestVoiceSearchMsg.time).getTime();
      const latestAgentMsg = [...latestRoom.msgHistory].reverse().find((msg) => {
        const isAgentMsg = msg.user === botName || msg.user.includes('voiceChat_');
        if (!isAgentMsg) return false;
        const msgTs = new Date(msg.time).getTime();
        return Number.isFinite(msgTs) && Number.isFinite(requestTs) ? msgTs > requestTs : true;
      });
      if (latestAgentMsg?.value) {
        // #region debug-point G:voice-search-watchdog-skip-replied
        reportDebugEvent(
          'G',
          'FocusChatRoom.tsx:voiceSearchWatchdog',
          '[DEBUG] Voice search watchdog skipped because agent already replied',
          {
            requestKey,
            latestAgentPreview: String(latestAgentMsg.value || '').slice(0, 200),
          }
        );
        // #endregion
        return;
      }
      const waitingMs = Number.isFinite(requestTs) ? Date.now() - requestTs : 0;
      if ((latestRoom.isAIThinking || latestRoom.isAITalking) && waitingMs < 6000) {
        // #region debug-point G:voice-search-watchdog-skip-active
        reportDebugEvent(
          'G',
          'FocusChatRoom.tsx:voiceSearchWatchdog',
          '[DEBUG] Voice search watchdog deferred because AI appears active',
          {
            requestKey,
            waitingMs,
            isAIThinking: latestRoom.isAIThinking,
            isAITalking: latestRoom.isAITalking,
          }
        );
        // #endregion
        return;
      }
      lastVoiceSearchFallbackKeyRef.current = requestKey;
      // #region debug-point G:voice-search-watchdog-fire
      reportDebugEvent(
        'G',
        'FocusChatRoom.tsx:voiceSearchWatchdog',
        '[DEBUG] Voice search watchdog firing fallback command',
        {
          requestKey,
          waitingMs,
          requestText: requestText.slice(0, 200),
          isAIThinking: latestRoom.isAIThinking,
          isAITalking: latestRoom.isAITalking,
        }
      );
      // #endregion
      recordSearchLatencyStart(requestKey, requestText, 'voice_watchdog');
      RtcClient.commandAgent({
        command: COMMAND.EXTERNAL_TEXT_TO_LLM,
        agentName: botName,
        interruptMode: INTERRUPT_PRIORITY.HIGH,
        message: buildAgentTextMessage(
          requestText,
          recentAiImageUrls,
          recentUserImageRequestContext
        ),
      });
    }, 1200);
    return () => {
      if (voiceSearchFallbackTimerRef.current) {
        window.clearTimeout(voiceSearchFallbackTimerRef.current);
        voiceSearchFallbackTimerRef.current = null;
      }
    };
  }, [
    botName,
    isVoiceConnected,
    recentAiImageUrls,
    recentUserImageRequestContext,
    recordSearchLatencyStart,
    room.isJoined,
    userId,
    visibleMessages,
  ]);

  useEffect(() => {
    if (!mobile || room.isJoined) {
      setMobileCompanionTrayOpen(false);
      return;
    }
    setMobileCompanionTrayOpen(false);
  }, [mobile, room.isJoined]);

  const clampCameraPanelRect = (nextRect: CameraPanelRect) => {
    const panel = chatPanelRef.current;
    if (!panel) return nextRect;
    const panelWidth = panel.clientWidth;
    const panelHeight = panel.clientHeight;
    const maxWidth = Math.min(CAMERA_PANEL_MAX_WIDTH, Math.max(CAMERA_PANEL_MIN_WIDTH, panelWidth));
    const width = Math.min(Math.max(nextRect.width, CAMERA_PANEL_MIN_WIDTH), maxWidth);
    const height = Math.round(width / CAMERA_PANEL_RATIO);
    const maxX = Math.max(0, panelWidth - width);
    const maxY = Math.max(0, panelHeight - height);
    return {
      x: Math.min(Math.max(nextRect.x, 0), maxX),
      y: Math.min(Math.max(nextRect.y, 0), maxY),
      width,
      height,
    };
  };

  const getDefaultCameraPanelRect = (): CameraPanelRect => {
    const panel = chatPanelRef.current;
    const offset = mobile ? CAMERA_PANEL_MOBILE_OFFSET : CAMERA_PANEL_DESKTOP_OFFSET;
    const preferredWidth = mobile
      ? CAMERA_PANEL_MOBILE_DEFAULT_WIDTH
      : CAMERA_PANEL_DESKTOP_DEFAULT_WIDTH;
    const fallbackRect = {
      x: offset,
      y: offset,
      width: preferredWidth,
      height: Math.round(preferredWidth / CAMERA_PANEL_RATIO),
    };
    if (!panel) return fallbackRect;
    const availableWidth = Math.max(CAMERA_PANEL_MIN_WIDTH, panel.clientWidth - offset * 2);
    const width = Math.min(
      CAMERA_PANEL_MAX_WIDTH,
      Math.max(CAMERA_PANEL_MIN_WIDTH, Math.min(preferredWidth, availableWidth))
    );
    const height = Math.round(width / CAMERA_PANEL_RATIO);
    return clampCameraPanelRect({
      x: panel.clientWidth - width - offset,
      y: offset,
      width,
      height,
    });
  };

  const updateCameraPanelRect = (nextRect: CameraPanelRect) => {
    setCameraPanelRect(clampCameraPanelRect(nextRect));
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [visibleMessages.length, room.isAITalking, room.isAIThinking]);

  useEffect(() => {
    cameraPanelRectRef.current = cameraPanelRect;
  }, [cameraPanelRect]);

  useEffect(() => {
    dictationModeRef.current = dictationMode;
  }, [dictationMode]);

  useEffect(() => {
    if (!isVoiceConnected || !isAudioPublished) {
      if (userVoiceHoldTimerRef.current) {
        window.clearTimeout(userVoiceHoldTimerRef.current);
        userVoiceHoldTimerRef.current = null;
      }
      setIsUserVoiceAnimating(false);
      return;
    }

    if (isUserVoiceDetected) {
      if (userVoiceHoldTimerRef.current) {
        window.clearTimeout(userVoiceHoldTimerRef.current);
        userVoiceHoldTimerRef.current = null;
      }
      setIsUserVoiceAnimating(true);
      return;
    }

    if (!isUserVoiceAnimating) return;
    userVoiceHoldTimerRef.current = window.setTimeout(() => {
      setIsUserVoiceAnimating(false);
      userVoiceHoldTimerRef.current = null;
    }, USER_VOICE_HOLD_MS);

    return () => {
      if (userVoiceHoldTimerRef.current) {
        window.clearTimeout(userVoiceHoldTimerRef.current);
        userVoiceHoldTimerRef.current = null;
      }
    };
  }, [isAudioPublished, isUserVoiceAnimating, isUserVoiceDetected, isVoiceConnected]);

  useEffect(() => {
    if (!showCameraPanel) {
      cameraInteractionRef.current = null;
      return;
    }
    updateCameraPanelRect(getDefaultCameraPanelRect());
    const handleWindowResize = () => {
      updateCameraPanelRect(cameraPanelRectRef.current);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [mobile, showCameraPanel]);

  useEffect(() => {
    if (!showCameraPanel) return;
    const handleMouseMove = (event: MouseEvent) => {
      const interaction = cameraInteractionRef.current;
      if (!interaction) return;
      if (interaction.type === 'drag') {
        updateCameraPanelRect({
          ...interaction.startRect,
          x: interaction.startRect.x + (event.clientX - interaction.pointerStartX),
          y: interaction.startRect.y + (event.clientY - interaction.pointerStartY),
        });
        return;
      }
      const deltaWidth = Math.max(
        event.clientX - interaction.pointerStartX,
        (event.clientY - interaction.pointerStartY) * CAMERA_PANEL_RATIO
      );
      const width = interaction.startRect.width + deltaWidth;
      updateCameraPanelRect({
        ...interaction.startRect,
        width,
        height: Math.round(width / CAMERA_PANEL_RATIO),
      });
    };
    const handleMouseUp = () => {
      cameraInteractionRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showCameraPanel]);

  useEffect(() => {
    return () => {
      try {
        dictationRecognitionRef.current?.abort();
      } catch (error) {
        // Ignore cleanup errors from browser speech recognition.
      }
    };
  }, []);

  useEffect(() => {
    if (!room.isJoined) {
      setLastUserActivityAt(null);
      return;
    }
    setLastUserActivityAt((prev) => prev ?? Date.now());
  }, [room.isJoined]);

  useEffect(() => {
    const localUserIds = new Set(
      [room.localUser.userId, RtcClient.basicInfo?.user_id, userId].filter(Boolean)
    );
    const latestLocalMsg = [...room.msgHistory].reverse().find((msg) => localUserIds.has(msg.user));

    if (!latestLocalMsg?.time) return;
    const activityAt = new Date(latestLocalMsg.time).getTime();
    if (!Number.isNaN(activityAt)) {
      setLastUserActivityAt(activityAt);
    }
  }, [room.msgHistory, room.localUser.userId, userId]);

  useEffect(() => {
    if (!room.isJoined || !lastUserActivityAt) return;
    const idleMs = 10 * 60 * 1000;
    const remainingMs = Math.max(0, idleMs - (Date.now() - lastUserActivityAt));
    const timer = window.setTimeout(async () => {
      const latestRoom = store.getState().room;
      if (!latestRoom.isJoined) return;
      if (latestRoom.isAITalking || latestRoom.isAIThinking) return;
      Message.info('长时间没有新消息，已自动结束本轮对话');
      await leaveRoom();
    }, remainingMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [lastUserActivityAt, leaveRoom, room.isAIThinking, room.isAITalking, room.isJoined]);

  const syncSceneSubtitleMode = (subtitleMode: number) => {
    if (room.sceneConfigMap[room.scene]?.subtitleMode === subtitleMode) return;
    dispatch(
      updateSceneConfig({
        ...room.sceneConfigMap,
        [room.scene]: {
          ...room.sceneConfigMap[room.scene],
          subtitleMode,
        },
      })
    );
  };

  const ensureAgentSubtitleMode = async (subtitleMode: number) => {
    if (currentSceneSubtitleMode === subtitleMode) return;
    RtcClient.subtitleMode = subtitleMode;
    syncSceneSubtitleMode(subtitleMode);
    if (!room.isJoined || !room.isAIGCEnable || !RtcClient.audioBotEnabled) return;
    await RtcClient.stopAgent(room.scene);
    await RtcClient.startAgent(room.scene);
  };

  const syncActiveCompanionToServer = async () => {
    if (!activeCompanion || !baseScene) return true;
    try {
      await Apis.Basic.saveSceneProfile(
        buildSceneProfilePayload(sceneId, baseScene, activeCompanion)
      );
      return true;
    } catch (error) {
      Message.error('同步伙伴配置失败，请稍后重试');
      return false;
    }
  };

  const dispatchJoin = async (options?: { enableMicOnJoin?: boolean }) => {
    if (!room.isJoined) {
      if (!loaded) {
        Message.info('伙伴加载中，请稍等...');
        return false;
      }
      if (!hasCompanions) {
        Message.info('请先创建伙伴');
        setDockVisible(true);
        return false;
      }
      const synced = await syncActiveCompanionToServer();
      if (!synced) return false;
    }
    return rawDispatchJoin(options);
  };

  const stopVoiceCall = async () => {
    try {
      setAgentPlaybackVolume(0);
      if (botName) {
        RtcClient.commandAgent({
          agentName: botName,
          command: COMMAND.AUDIO_CTRL,
          extraPayload: {
            AudioConfig: {
              Action: 'pause',
            },
          },
        });
      }
      setVoiceCallModeEnabled(false);
      if (isCameraEnabled) {
        await RtcClient.updateVisionConfig(sceneId, {
          Enable: false,
          SnapshotConfig: {
            StreamType: 0,
          },
        });
        await switchCamera(true);
      }
      if (isAudioPublished) {
        await switchMic(true);
      }
      return true;
    } catch (error) {
      Message.error('结束通话失败，请稍后重试');
      return false;
    }
  };

  const handleCallAction = async () => {
    if (isVoiceConnected) {
      await stopVoiceCall();
      return;
    }
    if (isCallSessionActive) {
      try {
        if (!isAudioPublished) {
          await switchMic(true);
        }
        if (botName) {
          RtcClient.commandAgent({
            agentName: botName,
            command: COMMAND.AUDIO_CTRL,
            extraPayload: {
              AudioConfig: {
                Action: 'play',
              },
            },
          });
        }
        setAgentPlaybackVolume(100, store.getState().room.remoteUsers);
        setVoiceCallModeEnabled(true);
      } catch (error) {
        Message.error('麦克风开启失败，请检查设备权限后重试');
      }
      return;
    }
    RtcClient.subtitleMode = VOICE_SUBTITLE_MODE;
    syncSceneSubtitleMode(VOICE_SUBTITLE_MODE);
    const joined = await dispatchJoin({ enableMicOnJoin: true });
    if (joined !== false) {
      setVoiceCallModeEnabled(true);
    }
  };

  const handleMicAction = async () => {
    if (!isVoiceConnected || joining) return;
    try {
      await switchMic(true);
    } catch (error) {
      Message.error(isAudioPublished ? '关闭麦克风失败，请稍后重试' : '开启麦克风失败，请稍后重试');
    }
  };

  const handleCameraAction = async () => {
    if (!showCameraControl || cameraBusy) return;
    const nextEnabled = !isCameraEnabled;
    try {
      setCameraBusy(true);
      if (nextEnabled) {
        updateCameraPanelRect(getDefaultCameraPanelRect());
      }
      await RtcClient.updateVisionConfig(sceneId, {
        Enable: nextEnabled,
        SnapshotConfig: {
          StreamType: 0,
        },
      });
      const switched = await switchCamera(true);
      if (!switched && nextEnabled) {
        await RtcClient.updateVisionConfig(sceneId, {
          Enable: false,
          SnapshotConfig: {
            StreamType: 0,
          },
        });
        Message.error('开启摄像头失败，请检查设备权限后重试');
      }
    } catch (error) {
      Message.error(nextEnabled ? '开启摄像头失败，请稍后重试' : '关闭摄像头失败，请稍后重试');
    } finally {
      setCameraBusy(false);
    }
  };

  const handleCameraPanelDragStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    cameraInteractionRef.current = {
      type: 'drag',
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      startRect: cameraPanelRectRef.current,
    };
  };

  const handleCameraPanelResizeStart = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    cameraInteractionRef.current = {
      type: 'resize',
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      startRect: cameraPanelRectRef.current,
    };
  };

  useEffect(() => {
    const handleResize = () => {
      setViewportSize(getViewportSize());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleStartNewConversation = async () => {
    setQuickMenuVisible(false);
    try {
      dictationRecognitionRef.current?.abort();
    } catch (error) {
      // Ignore cleanup errors from browser speech recognition.
    }
    dictationRecognitionRef.current = null;
    dictationBaseDraftRef.current = '';
    dictationCommittedTextRef.current = '';
    dictationSegmentRef.current = '';
    dictationShouldAutoSendRef.current = false;
    setDictationMode('idle');
    setDraft('');
    setPendingImages([]);
    if (room.isJoined) {
      await leaveRoom();
    }
  };

  const resetDictationState = (nextDraft = '') => {
    dictationRecognitionRef.current = null;
    dictationShouldAutoSendRef.current = false;
    dictationBaseDraftRef.current = '';
    dictationCommittedTextRef.current = '';
    dictationSegmentRef.current = '';
    setDictationMode('idle');
    setDraft(nextDraft);
  };

  const ensureConversationReady = async () => {
    if (room.isJoined && room.isAIGCEnable && RtcClient.audioBotEnabled) {
      return true;
    }
    const joined = await dispatchJoin({ enableMicOnJoin: false });
    if (!joined || !RtcClient.audioBotEnabled) {
      Message.error('建立文本会话失败，请稍后重试');
      return false;
    }
    return true;
  };

  async function waitForAgentReady() {
    const timeoutMs = 8000;
    const intervalMs = 120;
    const start = Date.now();
    const isAgentPresent = () =>
      store
        .getState()
        .room.remoteUsers.some(
          (remoteUser) =>
            remoteUser.username === botName ||
            remoteUser.userId === botName ||
            remoteUser.userId?.includes('voiceChat_')
        );

    while (!isAgentPresent() && Date.now() - start < timeoutMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, intervalMs);
      });
    }
    return isAgentPresent();
  }

  const sendImageDataUrlsToAgent = async (imageDataUrls: string[], message: string) => {
    if (!botName || !imageDataUrls.length) return;
    const preparedImages = imageDataUrls.map((dataUrl, index) => ({
      imageId: index + 1,
      fragments: splitIntoFragments(dataUrlToBase64(dataUrl), IMAGE_FRAGMENT_SIZE),
    }));
    const totalFragments = preparedImages.reduce((sum, image) => sum + image.fragments.length, 0);
    const groupId = ++imageGroupIdRef.current;

    for (const image of preparedImages) {
      for (let fragmentIndex = 0; fragmentIndex < image.fragments.length; fragmentIndex += 1) {
        const isLastFragment =
          image.imageId === preparedImages[preparedImages.length - 1].imageId &&
          fragmentIndex === image.fragments.length - 1;
        const isPartial = image.fragments.length > 1 && fragmentIndex < image.fragments.length - 1;
        const imageConfig: Record<string, unknown> = {
          Action: 'insert',
          GroupID: groupId,
          ImageType: 'base64',
          Images: [image.fragments[fragmentIndex]],
          Total: totalFragments,
          ImageID: image.imageId,
          IsPartial: isPartial,
        };
        if (image.fragments.length > 1) {
          imageConfig.FragmentID = fragmentIndex + 1;
        }

        RtcClient.commandAgent({
          command: COMMAND.EXTERNAL_TEXT_TO_LLM,
          agentName: botName,
          interruptMode: INTERRUPT_PRIORITY.HIGH,
          message: isLastFragment ? message : '',
          extraPayload: {
            ImageConfig: imageConfig,
          },
        });

        if (!isLastFragment) {
          // Stagger fragments slightly to avoid bursting binary messages.
          // eslint-disable-next-line no-await-in-loop
          await sleep(IMAGE_SEND_INTERVAL_MS);
        }
      }
    }
  };

  const sendTextOnly = async (text: string) => {
    const normalized = withSentenceEnding(text);
    const agentMessage = buildAgentTextMessage(
      text,
      recentAiImageUrls,
      recentUserImageRequestContext
    );
    if (!normalized) return;
    RtcClient.subtitleMode = FAST_TEXT_SUBTITLE_MODE;
    syncSceneSubtitleMode(FAST_TEXT_SUBTITLE_MODE);
    const ready = await ensureConversationReady();
    if (!ready) return;
    await ensureAgentSubtitleMode(FAST_TEXT_SUBTITLE_MODE);
    const agentReady = await waitForAgentReady();
    if (!agentReady) {
      Message.error('AI 还没有准备好，请稍后重试');
      return;
    }
    const latestRoom = store.getState().room;
    const localMessageUserId =
      RtcClient.basicInfo?.user_id || latestRoom.localUser.userId || userId || 'you';
    const localMessageTime = createMessageTimestamp();
    setLastUserActivityAt(Date.now());
    dispatch(
      appendLocalMsg({
        value: normalized,
        user: localMessageUserId,
        paragraph: true,
        definite: true,
        time: localMessageTime,
        source: 'manual',
      })
    );
    if (isWebSearchPendingText(text)) {
      recordSearchLatencyStart(
        buildSearchLatencyRequestKey({
          user: localMessageUserId,
          time: localMessageTime,
          value: normalized,
        }),
        normalized,
        'manual_text'
      );
    }
    RtcClient.commandAgent({
      command: COMMAND.EXTERNAL_TEXT_TO_LLM,
      agentName: botName,
      interruptMode: INTERRUPT_PRIORITY.HIGH,
      message: agentMessage,
    });
  };

  const sendImagesWithPrompt = async (images: PendingImage[], text: string) => {
    const normalized = withSentenceEnding(text);
    const agentMessage = buildAgentTextMessage(
      text,
      recentAiImageUrls,
      recentUserImageRequestContext
    );
    RtcClient.subtitleMode = FAST_TEXT_SUBTITLE_MODE;
    syncSceneSubtitleMode(FAST_TEXT_SUBTITLE_MODE);
    const ready = await ensureConversationReady();
    if (!ready) return;
    await ensureAgentSubtitleMode(FAST_TEXT_SUBTITLE_MODE);
    const agentReady = await waitForAgentReady();
    if (!agentReady) {
      Message.error('AI 还没有准备好，请稍后重试');
      return;
    }
    const latestRoom = store.getState().room;
    const localMessageUserId =
      RtcClient.basicInfo?.user_id || latestRoom.localUser.userId || userId || 'you';
    const localMessageTime = createMessageTimestamp();
    setLastUserActivityAt(Date.now());
    dispatch(
      appendLocalMsg({
        value: normalized,
        user: localMessageUserId,
        paragraph: true,
        definite: true,
        images: images.map((item) => item.dataUrl),
        time: localMessageTime,
        source: 'manual',
      })
    );
    if (isWebSearchPendingText(text)) {
      recordSearchLatencyStart(
        buildSearchLatencyRequestKey({
          user: localMessageUserId,
          time: localMessageTime,
          value: normalized,
        }),
        normalized,
        'manual_image'
      );
    }

    await sendImageDataUrlsToAgent(
      images.map((item) => item.dataUrl),
      agentMessage
    );
  };

  const submitDraftContent = async (text: string, images: PendingImage[]) => {
    const normalizedText = text.trim();
    if (!normalizedText && images.length === 0) return false;
    if (!hasCompanions && !room.isJoined) {
      Message.info('请先创建伙伴');
      setDockVisible(true);
      return false;
    }
    if (images.length > 0) {
      if (!isVision) {
        Message.warning('当前角色还没有开启图片理解能力，请先打开视觉能力配置');
        return false;
      }
      await sendImagesWithPrompt(images, normalizedText);
      return true;
    }
    await sendTextOnly(normalizedText);
    return true;
  };

  const handleSend = async () => {
    if (sending) return;
    if (dictationMode === 'listening') return;
    const text = draft.trim();
    if (!text && pendingImages.length === 0) return;
    try {
      setSending(true);
      const sent = await submitDraftContent(text, pendingImages);
      if (!sent) return;
      setPendingImages([]);
      resetDictationState('');
    } catch (error) {
      Message.error('发送失败，请稍后重试');
    } finally {
      setSending(false);
    }
  };

  const syncDraftWithDictation = (segment: string) => {
    dictationSegmentRef.current = segment;
    setDraft(mergeDraftWithDictation(dictationBaseDraftRef.current, segment));
  };

  const finalizeDictation = () => {
    const composedDraft = mergeDraftWithDictation(
      dictationBaseDraftRef.current,
      dictationSegmentRef.current
    );
    setDraft(composedDraft);
    setDictationMode('idle');
    return composedDraft;
  };

  const stopDictationRecognition = () => {
    const recognition = dictationRecognitionRef.current;
    if (!recognition) return;
    try {
      recognition.stop();
    } catch (error) {
      finalizeDictation();
      dictationRecognitionRef.current = null;
    }
  };

  const startDictationRecognition = (baseDraft: string) => {
    const SpeechRecognitionCtor = resolveSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) {
      Message.info('当前浏览器暂不支持语音输入，请使用 Chrome 后再试');
      return false;
    }

    const recognition = new SpeechRecognitionCtor();
    dictationBaseDraftRef.current = baseDraft;
    dictationCommittedTextRef.current = '';
    dictationSegmentRef.current = '';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'zh-CN';
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      if (dictationRecognitionRef.current !== recognition) return;
      let nextCommitted = dictationCommittedTextRef.current;
      let nextInterim = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript || '';
        if (!transcript) continue;
        if (result.isFinal) {
          nextCommitted += transcript;
        } else {
          nextInterim += transcript;
        }
      }
      dictationCommittedTextRef.current = nextCommitted;
      syncDraftWithDictation(`${nextCommitted}${nextInterim}`);
    };
    recognition.onerror = (event) => {
      if (dictationRecognitionRef.current !== recognition) return;
      dictationRecognitionRef.current = null;
      dictationShouldAutoSendRef.current = false;
      if (event.error !== 'aborted') {
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          Message.error('没有麦克风权限，无法使用听写');
        } else if (event.error === 'no-speech') {
          Message.info('没有识别到语音，再试一次吧');
        } else {
          Message.error('听写失败，请稍后重试');
        }
      }
      finalizeDictation();
    };
    recognition.onend = async () => {
      if (dictationRecognitionRef.current !== recognition) return;
      dictationRecognitionRef.current = null;
      if (dictationModeRef.current === 'listening') {
        const composedDraft = finalizeDictation();
        const shouldAutoSend = dictationShouldAutoSendRef.current;
        dictationShouldAutoSendRef.current = false;
        if (!shouldAutoSend || !composedDraft.trim()) return;
        try {
          setSending(true);
          const sent = await submitDraftContent(composedDraft, []);
          if (!sent) return;
          resetDictationState('');
        } catch (error) {
          Message.error('发送失败，请稍后重试');
        } finally {
          setSending(false);
        }
      }
    };
    dictationRecognitionRef.current = recognition;
    setDictationMode('listening');
    recognition.start();
    return true;
  };

  const restartDictationFromEditedDraft = (nextBaseDraft: string) => {
    const activeRecognition = dictationRecognitionRef.current;
    dictationRecognitionRef.current = null;
    dictationShouldAutoSendRef.current = false;
    try {
      activeRecognition?.abort();
    } catch (error) {
      // Ignore cleanup errors from browser speech recognition.
    }
    window.setTimeout(() => {
      try {
        startDictationRecognition(nextBaseDraft);
      } catch (error) {
        resetDictationState(nextBaseDraft);
        Message.error('听写恢复失败，请重试');
      }
    }, 0);
  };

  const handleDictationAction = async () => {
    if (dictationMode === 'listening') {
      dictationShouldAutoSendRef.current = true;
      stopDictationRecognition();
      return;
    }

    if (isVoiceConnected || joining) return;

    if (!hasCompanions && !room.isJoined) {
      Message.info('请先创建伙伴');
      setDockVisible(true);
      return;
    }
    if (pendingImages.length > 0) {
      Message.info('听写时请先发送或移除图片');
      return;
    }

    try {
      startDictationRecognition(draft);
    } catch (error) {
      dictationRecognitionRef.current = null;
      dictationShouldAutoSendRef.current = false;
      setDictationMode('idle');
      Message.error('听写启动失败，请稍后重试');
    }
  };

  const handleCancelDictation = () => {
    dictationShouldAutoSendRef.current = false;
    setDictationMode('idle');
    setDraft(dictationBaseDraftRef.current);
    try {
      dictationRecognitionRef.current?.abort();
    } catch (error) {
      // Ignore cleanup errors from browser speech recognition.
    }
    dictationRecognitionRef.current = null;
    dictationCommittedTextRef.current = '';
    dictationSegmentRef.current = '';
    dictationBaseDraftRef.current = '';
  };

  const handleQuickMenuAction = (action: 'image' | 'file') => {
    setQuickMenuVisible(false);
    if (action === 'image') {
      fileInputRef.current?.click();
      return;
    }
    Message.info('发送文件功能开发中');
  };

  const handlePickImages: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      const nextImages = await Promise.all(
        files.map(async (file, index) => ({
          id: `${Date.now()}-${index}`,
          dataUrl: await compressImage(file),
        }))
      );
      setPendingImages((prev) => [...prev, ...nextImages]);
    } catch (error) {
      Message.error('图片处理失败，请换一张试试');
    } finally {
      event.target.value = '';
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleDraftChange = (value: string) => {
    if (dictationMode === 'listening') {
      restartDictationFromEditedDraft(value);
    }
    setDraft(value);
  };

  const openCreateModal = () => {
    if (!canAddMore) {
      Message.info(`最多可创建 ${MAX_COMPANIONS} 个伙伴`);
      return;
    }
    setEditingCompanion(null);
    setShowCompanionSettings(true);
  };

  const openEditModal = (profile?: CompanionProfile | null) => {
    setEditingCompanion(profile || resolvedCompanion);
    setShowCompanionSettings(true);
  };

  const handleSaveCompanion = async (draft: CompanionDraft, editingId?: string) => {
    setSubmittingCompanion(true);
    const result = saveCompanion(draft, editingId);
    setSubmittingCompanion(false);
    if (!result.ok) {
      Message.warning(
        result.reason === 'limit' ? `最多可创建 ${MAX_COMPANIONS} 个伙伴` : '伙伴保存失败'
      );
      return;
    }
    Message.success(editingId ? '伙伴已更新' : '伙伴已创建');
    setShowCompanionSettings(false);
    setEditingCompanion(null);
  };

  const handleDeleteCompanion = (id: string) => {
    const profile = profiles.find((item) => item.id === id);
    Modal.confirm({
      title: '删除伙伴',
      content: `确认删除“${getCompanionDisplayName(profile)}”吗？`,
      okText: '删除',
      cancelText: '取消',
      onOk: () => {
        deleteCompanion(id);
        if (editingCompanion?.id === id) {
          setShowCompanionSettings(false);
          setEditingCompanion(null);
        }
      },
    });
  };

  const topRailHintText = '像聊天窗口一样查看完整上下文，适合认真聊事、追问和发图';
  const companionEmptyText = `先创建伙伴，再开始${desktopCompanion ? '桌宠' : '普通'}模式`;
  const roleDescText =
    !hasCompanions && !room.isJoined
      ? '从左侧滑出列表，创建并保存你的伙伴'
      : room.isAITalking
      ? '正在回答'
      : room.isAIThinking
      ? pendingTaskHint.title
      : '可通过文字、图片或语音和它对话';
  const emptyTitleText = desktopCompanion ? '我就在旁边。' : '开始第一句对话吧';
  const emptyDescText = desktopCompanion
    ? '想到什么就聊聊，也可以直接开口说。'
    : '你可以输入文字、图片、语音消息，或者彻底解放双手，实时对话。';
  return (
    <div
      className={`${styles.wrapper} ${desktopCompanion ? styles.desktopCompanion : ''}`}
      style={
        bgImage
          ? {
              backgroundImage: `linear-gradient(rgba(247,245,255,0.34), rgba(247,245,255,0.34)), url(${bgImage})`,
            }
          : undefined
      }
    >
      <div className={`${styles.topRail} ${mobile && room.isJoined ? styles.topRailInCall : ''}`}>
        {mobile ? (
          <button
            type="button"
            className={`${styles.topRailSecondaryBtn} ${
              mobileModeTrayOpen ? styles.topRailSecondaryBtnActive : ''
            }`}
            onClick={() =>
              setMobileModeTrayOpen((prev) => {
                const next = !prev;
                if (next) setMobileCompanionTrayOpen(false);
                return next;
              })
            }
          >
            模式切换
          </button>
        ) : (
          <div className={styles.topRailPrimary}>
            <ModeSwitch compact className={styles.modeSwitch} />
            <div className={styles.topRailHint}>{topRailHintText}</div>
          </div>
        )}
        {mobile && desktopCompanion && !room.isJoined ? (
          <button
            type="button"
            className={`${styles.topRailSecondaryBtn} ${
              mobileCompanionTrayOpen ? styles.topRailSecondaryBtnActive : ''
            }`}
            onClick={() =>
              setMobileCompanionTrayOpen((prev) => {
                const next = !prev;
                if (next) setMobileModeTrayOpen(false);
                return next;
              })
            }
          >
            {mobileCompanionTrayOpen ? '收起伙伴' : hasCompanions ? '展开伙伴' : '创建伙伴'}
          </button>
        ) : null}
      </div>
      {mobile ? (
        <div
          className={`${styles.mobileModeTray} ${
            mobileModeTrayOpen ? styles.mobileModeTrayOpen : ''
          }`}
        >
          <ModeSwitch compact ghost className={styles.modeSwitch} />
        </div>
      ) : null}
      {mobile && desktopCompanion && !room.isJoined ? (
        <div
          className={`${styles.mobileCompanionTray} ${
            mobileCompanionTrayOpen ? styles.mobileCompanionTrayOpen : ''
          }`}
        >
          <CompanionDock
            visible={mobileCompanionTrayOpen}
            layout="arc"
            dense
            profiles={profiles}
            activeId={activeId}
            canAddMore={canAddMore}
            emptyText={companionEmptyText}
            onSelect={(id) => {
              selectCompanion(id);
              setMobileCompanionTrayOpen(false);
            }}
            onAdd={openCreateModal}
            onDelete={handleDeleteCompanion}
          />
        </div>
      ) : null}

      <div className={styles.main}>
        {desktopCompanion && (
          <aside className={styles.petColumn} aria-label="桌宠伙伴">
            <div className={styles.petTopline}>
              <span>陪你待一会儿</span>
              <PetCompanionPicker
                open={dockVisible}
                disabled={room.isJoined}
                profiles={profiles}
                activeId={activeId}
                canAddMore={canAddMore}
                onOpenChange={setDockVisible}
                onSelect={selectCompanion}
                onAdd={openCreateModal}
                onDelete={handleDeleteCompanion}
              />
            </div>
            <div key={activeId} className={styles.petStage}>
              {resolvedCompanion && (
                <ResizableStage
                  storageKey="voxie_pet_scale_v1"
                  className={styles.petResizer}
                  min={0.6}
                  max={2.4}
                  origin="bottom center"
                  handleLabel="拖拽调整桌宠大小"
                  handleRect={petContentRect}
                  resizable={!mobile}
                >
                  <CompanionAvatar
                    avatar={resolvedCompanion.avatar}
                    portraitFrames={resolvedCompanion.portraitFrames}
                    live2dModelUrl={resolvedCompanion.live2dModelUrl}
                    className={styles.petAvatar}
                    isTalking={room.isAITalking}
                    volume={Math.max(
                      0,
                      ...room.remoteUsers.map((user) => user.audioPropertiesInfo?.linearVolume || 0)
                    )}
                    fit="contain"
                    objectPosition="center bottom"
                    disableLive2d={mobile}
                    onContentRect={setPetContentRect}
                    alt={`桌宠${displayName}`}
                  />
                </ResizableStage>
              )}
            </div>
            <div className={styles.petIdentity}>
              <button
                type="button"
                disabled={room.isJoined || !resolvedCompanion}
                onClick={() => openEditModal()}
                aria-label={`编辑伙伴：${displayName}`}
                title={room.isJoined ? '通话结束后可编辑伙伴' : '编辑伙伴'}
              >
                {displayName}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="m15 5 4 4M4 20l4.5-1L20 7.5a2.8 2.8 0 0 0-4-4L4.5 15 4 20Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span>
                <i />
                {room.isAITalking ? '正在和你说话' : room.isAIThinking ? '让我想一想' : '我在这里'}
              </span>
            </div>
          </aside>
        )}
        <div className={styles.chatPanel} ref={chatPanelRef}>
          {showCameraPanel ? (
            <div
              className={styles.floatingCameraShell}
              onMouseDown={handleCameraPanelDragStart}
              style={{
                left: `${cameraPanelRect.x}px`,
                top: `${cameraPanelRect.y}px`,
                width: `${cameraPanelRect.width}px`,
                height: `${cameraPanelRect.height}px`,
              }}
            >
              <CameraArea className={styles.focusCameraArea} fillContainer />
              <div
                className={styles.floatingCameraDragOverlay}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  cursor: 'move',
                  touchAction: 'none',
                }}
              />
              <button
                type="button"
                className={styles.floatingCameraResizeHandle}
                onMouseDown={handleCameraPanelResizeStart}
                aria-label="拖拽调整视频画面大小"
                title="拖拽调整大小"
              />
            </div>
          ) : null}
          <div className={styles.chatHeader}>
            {!desktopCompanion ? (
              <NormalCompanionPicker
                profiles={profiles}
                activeId={resolvedCompanion?.id}
                name={displayName}
                avatar={displayAvatar}
                description={roleDescText}
                disabled={room.isJoined || joining}
                canAddMore={canAddMore}
                onSelect={selectCompanion}
                onEdit={() => openEditModal()}
                onAdd={openCreateModal}
                onDelete={handleDeleteCompanion}
              />
            ) : (
              <div className={styles.roleMeta}>
                <button
                  type="button"
                  className={`${styles.roleAvatar} ${
                    !room.isJoined && resolvedCompanion ? styles.roleAvatarEditable : ''
                  }`}
                  onClick={() => {
                    if (!room.isJoined && resolvedCompanion) {
                      openEditModal();
                    }
                  }}
                  disabled={room.isJoined || !resolvedCompanion}
                  title={!room.isJoined && resolvedCompanion ? '编辑角色' : undefined}
                  aria-label={!room.isJoined && resolvedCompanion ? `编辑${displayName}` : displayName}
                >
                  {displayAvatar ? (
                    <img src={displayAvatar} alt={displayName || 'avatar'} />
                  ) : (
                    <span className={styles.roleAvatarInitial}>{displayAvatarInitial}</span>
                  )}
                </button>
                <div>
                  <div className={styles.roleName}>{displayName}</div>
                  <div className={styles.roleDesc}>{roleDescText}</div>
                </div>
              </div>
            )}
            <div className={styles.headerActions}>
              {room.isJoined || visibleMessages.length ? (
                <button
                  type="button"
                  className={styles.resetBtn}
                  onClick={handleStartNewConversation}
                >
                  开启新对话
                </button>
              ) : null}
            </div>
          </div>

          <div className={styles.messages} ref={scrollRef}>
            {!visibleMessages.length ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyTitle}>{emptyTitleText}</div>
                <div className={styles.emptyDesc}>{emptyDescText}</div>
              </div>
            ) : null}
            {visibleMessages.map((msg, index) => {
              const isUser = msg.user === userId;
              const isStableSubtitleMessage =
                msg.source !== 'subtitle' || isUser || Boolean(msg.definite || msg.paragraph);
              const { textWithoutImageDirectives, imageUrls, hasPendingBracketImageUrl } =
                parseMessageContent(msg.value || '');
              // 文本剥光 markdown / 括号 URL 后，可能仍残留纯 URL 或标点；这些不算可见内容，避免渲染空灰卡。
              const visibleText = textWithoutImageDirectives
                .replace(URL_PATTERN, '')
                .replace(/[\s\p{P}]+/gu, '')
                .trim();
              const shouldRenderGeneratedImages =
                isStableSubtitleMessage && !hasPendingBracketImageUrl && imageUrls.length > 0;
              const hasPrimaryCardContent = Boolean(msg.images?.length) || Boolean(visibleText);
              const shouldRenderPrimaryCard = hasPrimaryCardContent;
              const renderedTextContent = visibleText
                ? renderMessageContent(msg.value || '')
                : null;
              if (!shouldRenderGeneratedImages && !msg.images?.length && !renderedTextContent) {
                return null;
              }
              return (
                <div
                  key={`${msg.user}-${index}`}
                  className={`${styles.messageRow} ${isUser ? styles.userRow : styles.aiRow}`}
                  role="group"
                  aria-label={`${isUser ? '你' : name || botName}的消息`}
                >
                  <div
                    className={`${styles.messageStack} ${
                      isUser ? styles.userStack : styles.aiStack
                    }`}
                  >
                    {shouldRenderPrimaryCard ? (
                      <div
                        className={`${styles.messageCard} ${
                          isUser ? styles.userCard : styles.aiCard
                        } ${shouldRenderGeneratedImages ? styles.messageSplitMediaTextCard : ''}`}
                      >
                        {msg.images?.length ? renderImageGrid(msg.images, setPreviewImage) : null}
                        {renderedTextContent}
                      </div>
                    ) : null}
                    {shouldRenderGeneratedImages ? (
                      <div
                        className={`${styles.messageCard} ${
                          isUser ? styles.userCard : styles.aiCard
                        } ${styles.messageMediaCard}`}
                      >
                        {renderImageGrid(imageUrls, setPreviewImage)}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {showPendingTaskCard ? (
              <div className={`${styles.messageRow} ${styles.systemStatusRow}`}>
                <span className={styles.systemStatusSpinner} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            ) : null}
          </div>

          <div className={styles.composer}>
            <div className={styles.composerRow}>
              <div className={styles.composerMain}>
                {pendingImages.length ? (
                  <div className={styles.pendingImages}>
                    {pendingImages.map((image) => (
                      <div key={image.id} className={styles.pendingItem}>
                        <img src={image.dataUrl} alt="pending upload" />
                        <button
                          type="button"
                          className={styles.pendingRemove}
                          onClick={() => {
                            setPendingImages((prev) => prev.filter((item) => item.id !== image.id));
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className={styles.composerInputRow}>
                  {dictationMode !== 'listening' ? (
                    <Popover
                      trigger="click"
                      position="tl"
                      className={styles.quickMenuPopover}
                      popupVisible={quickMenuVisible}
                      onVisibleChange={setQuickMenuVisible}
                      content={
                        <div className={`${styles.quickMenu} ${desktopCompanion ? styles.quickMenuDesktop : ''}`}>
                          <button
                            type="button"
                            className={styles.quickMenuItem}
                            onClick={() => handleQuickMenuAction('image')}
                          >
                            <span
                              className={`${styles.quickMenuIcon} ${styles.quickMenuIconImage}`}
                            />
                            <span className={styles.quickMenuLabel}>添加图片</span>
                          </button>
                          <button
                            type="button"
                            className={styles.quickMenuItem}
                            onClick={() => handleQuickMenuAction('file')}
                          >
                            <span
                              className={`${styles.quickMenuIcon} ${styles.quickMenuIconFile}`}
                            />
                            <span className={styles.quickMenuLabel}>发送文件</span>
                          </button>
                        </div>
                      }
                    >
                      <button
                        type="button"
                        className={styles.plusBtn}
                        aria-label="更多操作"
                        aria-expanded={quickMenuVisible}
                        title="更多操作"
                      >
                        <span className={styles.plusIcon} aria-hidden="true" />
                      </button>
                    </Popover>
                  ) : null}
                  <Input.TextArea
                    value={draft}
                    onChange={handleDraftChange}
                    onKeyDown={handleKeyDown}
                    className={styles.textarea}
                    placeholder={
                      dictationMode === 'listening'
                        ? '正在听写，语音会实时转成文字'
                        : '有事儿，尽管说'
                    }
                    autoSize={{ minRows: 1, maxRows: 6 }}
                  />
                </div>
              </div>
              <div className={styles.actionGroup}>
                {dictationMode !== 'listening' ? (
                  <button
                    type="button"
                    className={`${styles.roundActionBtn} ${styles.sendBtn}`}
                    onClick={handleSend}
                    disabled={sending || (!draft.trim() && pendingImages.length === 0)}
                    aria-label="发送"
                    title="发送"
                  >
                    <span className={styles.sendIcon} />
                  </button>
                ) : null}
                {dictationMode === 'listening' ? (
                  <button
                    type="button"
                    className={`${styles.roundActionBtn} ${styles.dictationCancelBtn}`}
                    onClick={handleCancelDictation}
                    aria-label="取消听写"
                    title="取消听写"
                  >
                    <span className={styles.dictationCancelIcon} aria-hidden="true" />
                  </button>
                ) : null}
                {showDictationControl ? (
                  <button
                    type="button"
                    className={`${styles.roundActionBtn} ${styles.dictationBtn} ${
                      dictationMode === 'listening' ? styles.dictationBtnListening : ''
                    }`}
                    onClick={handleDictationAction}
                    aria-label={dictationMode === 'listening' ? '结束听写并发送' : '语音输入'}
                    title={dictationMode === 'listening' ? '结束听写并发送' : '语音输入'}
                  >
                    {dictationMode === 'listening' ? (
                      <span className={styles.dictationBreath} aria-hidden="true" />
                    ) : (
                      <span className={styles.dictationMic} aria-hidden="true" />
                    )}
                  </button>
                ) : null}
                {showCameraControl ? (
                  <button
                    type="button"
                    className={`${styles.roundActionBtn} ${styles.cameraBtn} ${
                      isCameraEnabled ? styles.cameraBtnActive : ''
                    }`}
                    onClick={handleCameraAction}
                    disabled={cameraBusy}
                    aria-label={isCameraEnabled ? '关闭摄像头' : '打开摄像头'}
                    title={isCameraEnabled ? '关闭摄像头' : '打开摄像头'}
                  >
                    <span className={styles.cameraIcon} aria-hidden="true" />
                  </button>
                ) : null}
                {showMicControl ? (
                  <button
                    type="button"
                    className={`${styles.roundActionBtn} ${styles.micBtn} ${
                      joining
                        ? styles.micBtnPending
                        : isAudioPublished
                        ? styles.micBtnActive
                        : styles.micBtnMuted
                    }`}
                    onClick={handleMicAction}
                    disabled={joining}
                    aria-label={joining ? 'AI 连接中' : isAudioPublished ? '闭麦' : '开麦'}
                    title={joining ? 'AI 连接中' : isAudioPublished ? '闭麦' : '开麦'}
                  >
                    <span className={styles.micIconWrap} aria-hidden="true">
                      <span className={styles.dictationMic} />
                      {!isAudioPublished || joining ? <span className={styles.micSlash} /> : null}
                    </span>
                  </button>
                ) : null}
                {showCallControl ? (
                  <button
                    type="button"
                    className={`${styles.callBtn} ${styles.callBtnIconOnly} ${
                      isVoiceConnected ? styles.callBtnActive : ''
                    }`}
                    onClick={handleCallAction}
                    aria-label={isVoiceConnected ? '结束通话' : '通话'}
                    title={isVoiceConnected ? '结束通话' : '通话'}
                  >
                    <span
                      className={`${styles.callWaves} ${
                        isVoiceConnected ? styles.callWavesConnected : callWaveMotionClass
                      }`}
                      aria-hidden="true"
                    >
                      <span />
                      <span />
                      <span />
                    </span>
                    {isVoiceConnected ? (
                      <span className={styles.callEndBadge} aria-hidden="true" />
                    ) : null}
                  </button>
                ) : null}
              </div>
            </div>
            <div className={styles.composerMeta}>
              <span className={!composerMetaText ? styles.composerMetaPlaceholder : undefined}>
                {composerMetaText || '占位'}
              </span>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePickImages}
            style={{ display: 'none' }}
          />
        </div>
      </div>
      <CompanionSettingsModal
        visible={showCompanionSettings}
        profile={editingCompanion}
        submitting={submittingCompanion}
        onCancel={() => {
          setShowCompanionSettings(false);
          setEditingCompanion(null);
        }}
        onSubmit={handleSaveCompanion}
      />
      <Modal
        visible={Boolean(previewImage)}
        footer={null}
        title="查看图片"
        onCancel={() => setPreviewImage(null)}
        className={styles.previewModal}
        unmountOnExit
      >
        {previewImage ? (
          <img src={previewImage} alt="preview" className={styles.previewImage} />
        ) : null}
      </Modal>
    </div>
  );
}
